// Turns the flat roster into families.
//
// `householdId` is stamped on players by the club's member import (see
// households.js); this module reads it back out and pairs each family with
// the sessions its members are booked into, so staff can see a whole family
// at once instead of reconstructing it from individual cards.
import { normalizeHouseholdId } from "./households";
import { ALL_DAYS } from "./tiers";
import { getGroupSchedule, formatRange } from "./time";

// Sessions are read off group.playerIds rather than player.groupId, because
// weekend Open Play and Private bookings never set a player's groupId — the
// same reason the weekly grid's member lookup reads them this way.
function sessionsFor(playerId, groups) {
  return groups
    .filter(g => (g.playerIds || []).includes(playerId))
    .sort((a, b) => ALL_DAYS.indexOf(a.day) - ALL_DAYS.indexOf(b.day));
}

// A family's display name. The surname shared by most of its members reads
// better than a bare membership number, but the number is what the club's
// file actually keys on, so both are kept.
function familyName(members) {
  const counts = new Map();
  members.forEach(m => {
    const last = String(m.player.lastName || "").trim() ||
      String(m.player.name || "").trim().split(/\s+/).slice(-1)[0] || "";
    if (last) counts.set(last, (counts.get(last) || 0) + 1);
  });
  let best = "";
  let bestN = 0;
  counts.forEach((n, last) => {
    if (n > bestN) { bestN = n; best = last; }
  });
  return best;
}

// "Monday · 6:00 PM–7:30 PM · Tennis Court 1". Goes through
// getGroupSchedule so legacy groups that still carry a single `time` range
// read the same as ones with startTime/duration.
export function describeSession(group) {
  if (!group) return "";
  const { startTime, duration } = getGroupSchedule(group);
  const when = startTime && duration ? formatRange(startTime, duration) : "";
  return [group.day, when, group.court].filter(Boolean).join(" · ");
}

// Orders households by membership number. `key` has already had leading
// zeros stripped, so "038" and "38" compare as 38 — without this they sort as
// text and #087 lands before #044. Ids that aren't numbers (the field is free
// text) fall to the end, alphabetically, rather than being compared as NaN.
export function compareHouseholdId(a, b) {
  const na = /^\d+$/.test(a.key) ? Number(a.key) : null;
  const nb = /^\d+$/.test(b.key) ? Number(b.key) : null;
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  return a.key.localeCompare(b.key);
}

// Everything the Households view needs, in one pass:
//   families  — households with 2+ members, the ones actually worth browsing
//   singles   — households of one
//   unlinked  — members carrying no household id at all
//
// Each household carries its members (with their sessions), the contact
// addresses anyone in it has, a total session count, and how many times a
// week the family has to be at the club — the roll-up that makes a family
// view more useful than reading cards one at a time.
export function buildHouseholds(players, groups) {
  const list = Array.isArray(players) ? players : [];
  const allGroups = Array.isArray(groups) ? groups : [];

  const byId = new Map();
  const unlinked = [];

  list.forEach(player => {
    const key = normalizeHouseholdId(player.householdId);
    const member = { player, sessions: sessionsFor(player.id, allGroups) };
    if (!key) {
      unlinked.push(member);
      return;
    }
    if (!byId.has(key)) {
      // Keep the id exactly as it was typed for display; `key` is only for
      // grouping, so "038" and "38" land in one family without the view
      // showing a number nobody recognises.
      byId.set(key, { key, rawId: String(player.householdId).trim(), members: [] });
    }
    byId.get(key).members.push(member);
  });

  const households = [...byId.values()].map(h => {
    const members = h.members.sort((a, b) => {
      // Adults first, then by age, so parents head the list and the kids
      // read in a predictable order.
      const ac = a.player.classification === "Adult" ? 0 : 1;
      const bc = b.player.classification === "Adult" ? 0 : 1;
      if (ac !== bc) return ac - bc;
      return String(a.player.name || "").localeCompare(String(b.player.name || ""));
    });

    const emails = [...new Set(
      members.map(m => String(m.player.email || "").trim()).filter(Boolean)
    )];

    // One session can hold two family members — siblings in the same clinic
    // are one trip, not two — so count distinct sessions, not bookings.
    const sessionIds = new Set();
    members.forEach(m => m.sessions.forEach(s => sessionIds.add(s.id)));

    const byDay = {};
    members.forEach(m => m.sessions.forEach(s => {
      if (!s.day) return;
      if (!byDay[s.day]) byDay[s.day] = new Set();
      byDay[s.day].add(s.id);
    }));
    const days = ALL_DAYS
      .filter(d => byDay[d]?.size)
      .map(d => ({ day: d, count: byDay[d].size }));

    return {
      ...h,
      members,
      emails,
      name: familyName(members),
      sessionCount: sessionIds.size,
      days,
      // Nobody in the family is booked into anything — worth surfacing,
      // since a family that signed up and never got placed is a miss.
      unplaced: members.filter(m => m.sessions.length === 0).length,
    };
  });

  const sortByName = (a, b) =>
    (a.name || a.rawId).localeCompare(b.name || b.rawId) || compareHouseholdId(a, b);

  return {
    families: households.filter(h => h.members.length > 1).sort(sortByName),
    singles: households.filter(h => h.members.length === 1).sort(sortByName),
    unlinked: unlinked.sort((a, b) =>
      String(a.player.lastName || a.player.name).localeCompare(String(b.player.lastName || b.player.name))
    ),
    // Every household id in use, for the "move into an existing family"
    // picker. Ordered by number, since the number is what the option leads
    // with — sorting these by surname makes the list look shuffled.
    allHouseholds: [...households].sort(compareHouseholdId),
  };
}

// Does this household match what was typed? Matches the family name, the
// membership number, any member's name, and any contact address — so a
// parent's email finds the family the same way the public lookup does.
export function householdMatches(household, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  if (String(household.name || "").toLowerCase().includes(q)) return true;
  if (String(household.rawId || "").toLowerCase().includes(q)) return true;
  if (household.emails.some(e => e.toLowerCase().includes(q))) return true;
  return household.members.some(m =>
    String(m.player.name || "").toLowerCase().includes(q)
  );
}
